import type { Layout, Config, PlotData } from "plotly.js";
import { PlotlyLib } from "./Plot";
import { trackFeature } from "./analytics";

export const WB_RED = "#c8102e";
export const WB_RED_HOT = "#a50d24";
export const WB_MUTED = "#6b7280";

const GRID = "rgba(0,0,0,0.06)";

export const plotlyBaseLayout: Partial<Layout> = {
  // 'simple_white' is Plotly's white-paper template — pinning it here
  // stops the export rasteriser from falling back to the 'plotly' default
  // template, whose plot_bgcolor is #E5ECF6 (the pale blue you see in
  // exported PNGs).
  template: "simple_white" as never,
  paper_bgcolor: "#ffffff",
  plot_bgcolor: "#ffffff",
  font: {
    // Pin to a system-wide family so Plotly's SVG→PNG export doesn't fall
    // back to its defaults (which include the #E5ECF6 pale-blue plot area
    // and Open Sans) when the page's webfont isn't loaded at export time.
    family: "Helvetica, Arial, sans-serif",
    color: "#111111",
    size: 11,
  },
  margin: { l: 50, r: 14, t: 10, b: 30 },
  modebar: {
    orientation: "h",
    bgcolor: "rgba(255,255,255,0)",
    color: "#6b7280",
    activecolor: "#c8102e",
  },
  xaxis: {
    gridcolor: GRID,
    gridwidth: 1,
    showline: false,
    zeroline: false,
    tickfont: { color: WB_MUTED, size: 10 },
    title: { font: { color: WB_MUTED, size: 10 } },
    // Compact single-line tickformat for time axes. At sub-day zooms
    // include the date alongside the time on every tick — Plotly
    // doesn't dedupe newline-separated formats, and a secondary axis
    // would cost vertical space. Plotly ignores these stops on numeric
    // axes (chainage etc.). dtickrange units are milliseconds.
    tickformatstops: [
      { dtickrange: [null, 1000],              value: "%H:%M:%S.%L %-d %b" },
      { dtickrange: [1000, 60_000],            value: "%H:%M:%S %-d %b" },
      { dtickrange: [60_000, 3_600_000],       value: "%H:%M %-d %b" },
      { dtickrange: [3_600_000, 86_400_000],   value: "%H:%M %-d %b" },
      { dtickrange: [86_400_000, 604_800_000], value: "%-d %b" },
      { dtickrange: [604_800_000, "M1"],       value: "%-d %b" },
      { dtickrange: ["M1", "M12"],             value: "%b %Y" },
      { dtickrange: ["M12", null],             value: "%Y" },
    ],
  },
  yaxis: {
    gridcolor: GRID,
    gridwidth: 1,
    showline: false,
    zeroline: false,
    tickfont: { color: WB_MUTED, size: 10 },
    title: { font: { color: WB_MUTED, size: 10 }, standoff: 6 },
  },
  hoverlabel: {
    bgcolor: "#111111",
    bordercolor: "#111111",
    font: { family: "IBM Plex Mono", color: "#ffffff", size: 11 },
  },
  showlegend: false,
  autosize: true,
};

// Default Plotly camera icon, copied from plotly.js source so we can
// reuse it on the custom download button without importing the icon set.
const CAMERA_ICON = {
  width: 875,
  height: 1000,
  path:
    "m500-17q-117 0-211-91-93-91-93-225t93-225q94-91 211-91 117 0 211 91 93 91 93 225t-93 225q-94 91-211 91z m0-742q-86 0-147 60-60 61-60 146 0 86 60 146 61 61 147 61 86 0 146-61 61-60 61-146 0-85-61-146-60-60-146-60z m530 442h-220v143h-153l-157 199-157-199h-153v-143h-220v-580h1060z",
  transform: "matrix(1 0 0 -1 0 850)",
};

/**
 * Build a per-PlotCard Plotly config. `title` is the visible HTML
 * heading on the card — Plotly normally has no idea about it (we
 * render titles in HTML, not in the SVG), so without baking it in here
 * the downloaded PNG arrives untitled. The download button also reads
 * the on-screen plot's dimensions so the exported image keeps the
 * same aspect ratio as what's on the page.
 */
export function buildPlotlyConfig(opts: { title?: string } = {}): Partial<Config> {
  const { title } = opts;
  return {
    displayModeBar: true,
    displaylogo: false,
    responsive: true,
    doubleClick: "reset",
    // Force one horizontal row with no group separators. Plotly's
    // default registry partitions buttons into 3 groups (download,
    // zoom, axis); on narrower plots those wrap into multiple
    // columns. A single outer group has nothing to wrap into.
    modeBarButtons: [[
      {
        name: "downloadPNGwhite",
        title: "Download plot as PNG",
        icon: CAMERA_ICON,
        click: (gd: HTMLElement) => {
          // Pull live dimensions off the on-page graph div so the
          // export aspect matches what the operator is looking at.
          // Fallback values keep the call safe if the div isn't sized
          // yet (it always should be, but defence in depth).
          const w = Math.max(600, Math.round(gd.clientWidth || 1200));
          const h = Math.max(300, Math.round(gd.clientHeight || 480));

          // Read the CURRENT card heading at click time. Plotly's
          // config diffing doesn't treat a new click closure as a
          // change, so this handler can outlive many re-renders and
          // the `title` captured at build time goes stale (e.g. the
          // Drive Parameters heading after toggling chips). PlotCard
          // mirrors its live heading into data-plot-title for exactly
          // this reason; the captured title is only a fallback for
          // plots not wrapped in a PlotCard.
          const liveTitle =
            gd.closest<HTMLElement>("[data-plot-title]")?.dataset.plotTitle ??
            title;

          // Snapshot the live layout/data. Adding `title` into the
          // exported figure (Plotly's SVG-side title) brings the
          // visible card heading back into the PNG, and re-asserting
          // paper/plot bgcolor here guards against the export
          // rasteriser falling back to the default pale-blue
          // template if `simple_white` was lost in the round-trip.
          const gdAny = gd as unknown as { data?: unknown; layout?: Record<string, unknown> };
          const baseLayout = (gdAny.layout ?? {}) as Record<string, unknown>;
          const baseMargin = (baseLayout.margin as Record<string, number> | undefined) ?? {};
          // Annotations pinned to the top edge of the plot area (e.g.
          // pipe-change labels at y=1) sit in the top margin band — the
          // same band the exported SVG title occupies. Detect them so
          // the title gets extra clearance instead of being overlapped.
          const baseAnnotations = Array.isArray(baseLayout.annotations)
            ? (baseLayout.annotations as Array<Record<string, unknown>>)
            : [];
          const hasTopAnnotations = baseAnnotations.some(
            (a) => a.yref === "paper" && typeof a.y === "number" && a.y >= 1,
          );
          const figure = {
            data: gdAny.data,
            layout: {
              ...baseLayout,
              template: "simple_white" as never,
              paper_bgcolor: "#ffffff",
              plot_bgcolor: "#ffffff",
              width: w,
              height: h,
              title: liveTitle
                ? {
                    text: liveTitle,
                    font: { size: 14, color: "#111111", family: "Helvetica, Arial, sans-serif" },
                    x: 0.5,
                    xanchor: "center" as const,
                    y: 0.97,
                    yanchor: "top" as const,
                  }
                : undefined,
              margin: {
                ...baseMargin,
                // Give Plotly's SVG title room without changing the
                // on-screen layout; widen further when top-edge
                // annotations would otherwise collide with it.
                t: liveTitle ? (hasTopAnnotations ? 72 : 44) : (baseMargin.t ?? 16),
              },
            },
          };

          // Name the file after the plot. Every export used to land as
          // "tbm_plot.png", so a batch became tbm_plot (1), (2), ...
          const slug = (liveTitle ?? "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "")
            .slice(0, 60);
          const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");

          trackFeature("plot_png_export");
          void PlotlyLib.downloadImage(figure as never, {
            format: "png",
            filename: slug ? `tbm_${slug}_${stamp}` : `tbm_plot_${stamp}`,
            width: w,
            height: h,
            scale: 2,
            setBackground: "#ffffff",
          });
        },
      },
      "zoom2d",
      "pan2d",
      "autoScale2d",
      "resetScale2d",
    ]] as Config["modeBarButtons"],
  };
}

/**
 * Legacy export kept so any caller that imported the plain `plotlyConfig`
 * keeps working. New code should use `buildPlotlyConfig({ title })`.
 */
export const plotlyConfig: Partial<Config> = buildPlotlyConfig();

export const primaryTrace: Partial<PlotData> = {
  type: "scatter",
  mode: "lines",
  line: { color: WB_RED, width: 1.2 },
  hovertemplate: "%{y:.3f}<extra></extra>",
};

export const secondaryTrace: Partial<PlotData> = {
  type: "scatter",
  mode: "lines",
  line: { color: "#4b5563", width: 1, dash: "dot" },
  hovertemplate: "%{y:.3f}<extra></extra>",
};
